'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

interface Operation {
  num: string;
  title: string;
  description: string;
  href: string;
  image: string;
  imageAlt: string;
}

const operations: Operation[] = [
{
  num: '01 / 03',
  title: 'Upstream',
  description: 'Exploration and production of crude oil from Lake Albert basin. Managing petroleum agreements and maximizing resource recovery for national benefit.',
  href: '/projects',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d573d8e2-1778365790383.png",
  imageAlt: 'UNOC upstream exploration and production activities at Lake Albert basin Uganda oil fields'
},
{
  num: '02 / 03',
  title: 'Midstream',
  description: 'Transportation infrastructure including the East African Crude Oil Pipeline (EACOP). Ensuring safe, efficient crude oil transportation from fields to markets.',
  href: '/projects/eacop',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18055a29f-1778365790624.png",
  imageAlt: 'East African Crude Oil Pipeline EACOP midstream infrastructure Uganda Tanzania crude oil transport'
},
{
  num: '03 / 03',
  title: 'Downstream',
  description: 'Refining operations at Kabaale Industrial Park. Processing crude oil into petroleum products for domestic consumption and regional export.',
  href: '/projects/refinery',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12b1be5ec-1778365791630.png",
  imageAlt: 'Uganda Oil Refinery downstream processing facility at Kabaale Industrial Park Hoima District'
}];


export default function OperationsSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div
          className="mb-16 max-w-3xl"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease'
          }}>
          
          <div className="flex items-center gap-3 mb-5">
            <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 01 ]</span>
            <div className="h-[1px] w-12 bg-[#CC0000]" />
            <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]" style={{ fontFamily: 'Inter, sans-serif' }}>Value Chain</span>
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0F0F] leading-[1.05] tracking-tight"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.025em' }}>
            
            Our <span className="text-[#CC0000]">Operations</span>
          </h2>
          <p className="text-lg text-gray-600 mt-5 max-w-2xl" style={{ fontFamily: 'Inter, sans-serif' }}>
            Integrated petroleum value chain management across Uganda&apos;s energy sector.
          </p>
        </div>

        {/* Operations grid */}
        <div className="grid md:grid-cols-3 gap-0 border-t border-l border-gray-200">
          {operations.map((op, i) =>
          <Link
            key={i}
            href={op.href}
            className="group relative bg-white border-r border-b border-gray-200 p-10 hover:bg-[#0F0F0F] transition-colors duration-300"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'none' : 'translateY(20px)',
              transition: `opacity 0.6s ease ${0.1 * (i + 1)}s, transform 0.6s ease ${0.1 * (i + 1)}s, background-color 0.3s ease`
            }}>
            
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono text-xs text-gray-400 group-hover:text-[#CC0000] tracking-[0.2em] transition-colors">
                  {op.num}
                </span>
                <div className="w-9 h-9 bg-[#CC0000] flex items-center justify-center rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M7 7h10v10" />
                    <path d="M7 17 17 7" />
                  </svg>
                </div>
              </div>

              {/* Image */}
              <div className="relative aspect-[16/9] overflow-hidden mb-8 rounded-sm">
                <img
                src={op.image}
                alt={op.imageAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-[#0F0F0F]/0 group-hover:bg-[#0F0F0F]/30 transition-colors duration-300" />
              </div>

              <h3
              className="text-2xl font-extrabold text-[#0F0F0F] group-hover:text-white mb-4 transition-colors"
              style={{ fontFamily: 'Inter, sans-serif' }}>
              
                {op.title}
              </h3>
              <p className="text-sm text-gray-600 group-hover:text-gray-300 leading-relaxed transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                {op.description}
              </p>

              {/* Arrow indicator */}
              <div className="mt-8 flex items-center gap-2 text-[#CC0000] opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-xs font-mono tracking-[0.15em] uppercase">Learn More</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 7h10v10" />
                  <path d="M7 17 17 7" />
                </svg>
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>);

}